import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
} from '@mui/material';
import { Male as MaleIcon, Female as FemaleIcon } from '@mui/icons-material';
import { dashboardAPI } from '../services/api';

const DRClassificationReport = ({ filters = {} }) => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReport();
  }, [filters]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const cleanParams = {};
      if (filters.region) cleanParams.region = filters.region;
      if (filters.facility_name) cleanParams.facility_name = filters.facility_name;
      if (filters.year && filters.year !== 'all_data') cleanParams.year = filters.year;
if (filters.gender) cleanParams.gender = filters.gender; //
      const response = await dashboardAPI.getDRClassificationReport(cleanParams);
      
      console.log('DR Report Response:', response); // Debug log
      
      // Handle the nested data structure
      const data = response?.data?.data || response?.data || [];
      
      // Ensure data is an array
      const dataArray = Array.isArray(data) ? data : [];
      
      setReportData(dataArray);
    } catch (err) {
      console.error('Error loading DR Classification Report:', err);
      setError('Failed to load DR Classification Report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals from the report data
// Calculate totals from the report data
// Calculate totals from the report data
const calculateTotals = () => {
  if (!Array.isArray(reportData) || reportData.length === 0) {
    return {
      type1_female: 0,
      type1_male: 0,
      type2_female: 0,
      type2_male: 0,
      non_dm_female: 0,
      non_dm_male: 0,
      total_female: 0,
      total_male: 0,
    };
  }

  return reportData.reduce(
    (acc, row) => ({
      type1_female: acc.type1_female + (parseInt(row.type1_female, 10) || 0),
      type1_male: acc.type1_male + (parseInt(row.type1_male, 10) || 0),
      type2_female: acc.type2_female + (parseInt(row.type2_female, 10) || 0),
      type2_male: acc.type2_male + (parseInt(row.type2_male, 10) || 0),
      non_dm_female: acc.non_dm_female + (parseInt(row.non_dm_female, 10) || 0),
      non_dm_male: acc.non_dm_male + (parseInt(row.non_dm_male, 10) || 0),
      total_female: acc.total_female + (parseInt(row.total_female, 10) || 0),
      total_male: acc.total_male + (parseInt(row.total_male, 10) || 0),
    }),
    {
      type1_female: 0,
      type1_male: 0,
      type2_female: 0,
      type2_male: 0,
      non_dm_female: 0,
      non_dm_male: 0,
      total_female: 0,
      total_male: 0,
    }
  );
};
  const totals = calculateTotals();

  const GenderChip = ({ count, gender }) => (
    <Chip
      icon={gender === 'F' ? <FemaleIcon sx={{ fontSize: 16 }} /> : <MaleIcon sx={{ fontSize: 16 }} />}
      label={count || 0}
      size="small"
      sx={{
        height: 24,
        fontSize: '0.75rem',
        fontWeight: 600,
        backgroundColor: gender === 'F' ? '#fce7f3' : '#dbeafe',
        color: gender === 'F' ? '#be185d' : '#1e40af',
        '& .MuiChip-icon': {
          color: gender === 'F' ? '#be185d' : '#1e40af',
        },
      }}
    />
  );

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress sx={{ color: '#667eea' }} />
          <Typography variant="body2" color="text.secondary">
            Loading DR Classification Report...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid #e5e7eb',
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!Array.isArray(reportData) || reportData.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid #e5e7eb',
        }}
      >
        <Alert severity="info">No DR classification data available for the selected filters.</Alert>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        background: 'white',
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
        DR Classification Report by Patient Type and Gender
      </Typography>

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{
                  fontWeight: 700,
                  backgroundColor: '#f8fafc',
                  borderRight: '2px solid #e2e8f0',
                  minWidth: 200,
                }}
              >
                DR Classification
              </TableCell>
              <TableCell
                colSpan={2}
                align="center"
                sx={{
                  fontWeight: 700,
                  backgroundColor: '#eff6ff',
                  borderRight: '2px solid #e2e8f0',
                }}
              >
                Type I DM Client
              </TableCell>
              <TableCell
                colSpan={2}
                align="center"
                sx={{
                  fontWeight: 700,
                  backgroundColor: '#fef3c7',
                  borderRight: '2px solid #e2e8f0',
                }}
              >
                Type II DM Client
              </TableCell>
              <TableCell
                colSpan={2}
                align="center"
                sx={{
                  fontWeight: 700,
                  backgroundColor: '#f0fdf4',
                  borderRight: '2px solid #e2e8f0',
                }}
              >
                Non-DM Client
              </TableCell>
              <TableCell
                colSpan={2}
                align="center"
                sx={{
                  fontWeight: 700,
                  backgroundColor: '#faf5ff',
                }}
              >
                Total
              </TableCell>
            </TableRow>
            <TableRow>
              {/* Type I DM */}
              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: '#eff6ff', fontSize: '0.75rem' }}>
                F
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 600,
                  backgroundColor: '#eff6ff',
                  borderRight: '2px solid #e2e8f0',
                  fontSize: '0.75rem',
                }}
              >
                M
              </TableCell>
              {/* Type II DM */}
              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: '#fef3c7', fontSize: '0.75rem' }}>
                F
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 600,
                  backgroundColor: '#fef3c7',
                  borderRight: '2px solid #e2e8f0',
                  fontSize: '0.75rem',
                }}
              >
                M
              </TableCell>
              {/* Non-DM */}
              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: '#f0fdf4', fontSize: '0.75rem' }}>
                F
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 600,
                  backgroundColor: '#f0fdf4',
                  borderRight: '2px solid #e2e8f0',
                  fontSize: '0.75rem',
                }}
              >
                M
              </TableCell>
              {/* Total */}
              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: '#faf5ff', fontSize: '0.75rem' }}>
                F
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: '#faf5ff', fontSize: '0.75rem' }}>
                M
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  '&:hover': { backgroundColor: '#f8fafc' },
                  '&:last-child td': { borderBottom: '2px solid #e2e8f0' },
                }}
              >
                <TableCell sx={{ fontWeight: 600, borderRight: '2px solid #e2e8f0' }}>
                  {row.classification}
                </TableCell>
                {/* Type I DM */}
                <TableCell align="center" sx={{ backgroundColor: '#f9fafb' }}>
                  <GenderChip count={row.type1_female} gender="F" />
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#f9fafb', borderRight: '2px solid #e2e8f0' }}>
                  <GenderChip count={row.type1_male} gender="M" />
                </TableCell>
                {/* Type II DM */}
                <TableCell align="center" sx={{ backgroundColor: '#fffbeb' }}>
                  <GenderChip count={row.type2_female} gender="F" />
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#fffbeb', borderRight: '2px solid #e2e8f0' }}>
                  <GenderChip count={row.type2_male} gender="M" />
                </TableCell>
                {/* Non-DM */}
                <TableCell align="center" sx={{ backgroundColor: '#f0fdf4' }}>
                  <GenderChip count={row.non_dm_female} gender="F" />
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#f0fdf4', borderRight: '2px solid #e2e8f0' }}>
                  <GenderChip count={row.non_dm_male} gender="M" />
                </TableCell>
                {/* Total */}
                <TableCell align="center" sx={{ backgroundColor: '#faf5ff' }}>
                  <GenderChip count={row.total_female} gender="F" />
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#faf5ff' }}>
                  <GenderChip count={row.total_male} gender="M" />
                </TableCell>
              </TableRow>
            ))}
            {/* Totals Row */}
            <TableRow
              sx={{
                backgroundColor: '#faf5ff',
                '& td': {
                  fontWeight: 700,
                  borderTop: '3px solid #7c3aed',
                  borderBottom: '3px solid #7c3aed',
                },
              }}
            >
              <TableCell sx={{ borderRight: '2px solid #e2e8f0', color: '#7c3aed' }}>TOTAL</TableCell>
              {/* Type I DM Totals */}
              <TableCell align="center">
                <GenderChip count={totals.type1_female} gender="F" />
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '2px solid #e2e8f0' }}>
                <GenderChip count={totals.type1_male} gender="M" />
              </TableCell>
              {/* Type II DM Totals */}
              <TableCell align="center">
                <GenderChip count={totals.type2_female} gender="F" />
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '2px solid #e2e8f0' }}>
                <GenderChip count={totals.type2_male} gender="M" />
              </TableCell>
              {/* Non-DM Totals */}
              <TableCell align="center">
                <GenderChip count={totals.non_dm_female} gender="F" />
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '2px solid #e2e8f0' }}>
                <GenderChip count={totals.non_dm_male} gender="M" />
              </TableCell>
              {/* Grand Totals */}
              <TableCell align="center">
                <GenderChip count={totals.total_female} gender="F" />
              </TableCell>
              <TableCell align="center">
                <GenderChip count={totals.total_male} gender="M" />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default DRClassificationReport;